import React, { ChangeEvent, Dispatch, FC, SetStateAction, useMemo } from 'react';
import {
  ForgeButtonToggle,
  ForgeButtonToggleGroup,
  ForgeIcon,
  ForgeOption,
  ForgeSelect,
  ForgeTextField
} from '@tylertech/forge-react';
import {
  DayTypes,
  FrequencyConfig,
  IntervalInfo,
  IntervalTypes,
  WeekTypes
} from 'common/types/frequencySelector/frequencySelector';
import { dayOptions, intervalOptions, weekOptions } from '../constants/options';
import { I18n } from 'common/visualizations';

const scope = 'shared.components.frequency_selector.interval';

export interface IntervalProps {
  value: IntervalInfo;
  setValue: Dispatch<SetStateAction<FrequencyConfig>>;
}

const Interval: FC<IntervalProps> = (props) => {
  const { value, setValue } = props;

  const shouldShowDaySelector = useMemo(() => value.type !== 'day', [value.type]);
  const shouldShowWeekSelector = useMemo(() => value.type === 'month', [value.type]);
  const canSelectMultipleDays = useMemo(() => value.type !== 'month', [value.type]);

  const resetRepeatOnFields = <K extends keyof IntervalInfo>(val: IntervalInfo[K]) => {
    const defaultDaysVal: DayTypes[] = ['mon'];
    const defaultWeekVal: WeekTypes = 'first';

    if (val === 'day') {
      return {
        ...(value.days?.length && { days: [] }),
        ...(value.week && { week: undefined })
      };
    } else if (val === 'week') {
      return {
        days: defaultDaysVal,
        ...(value.week && { week: undefined })
      };
    } else if (val === 'month') {
      return {
        days: defaultDaysVal,
        ...(!value.week && { week: defaultWeekVal })
      };
    }
  };

  const onValueChange = <K extends keyof IntervalInfo>(field: K, val: IntervalInfo[K]) => {
    let updatedFormValue: Partial<IntervalInfo> = {
      [field]: val
    };

    //Reset fields on specific conditions
    if (field === 'type') updatedFormValue = { ...updatedFormValue, ...resetRepeatOnFields(val) };

    setValue((prevState) => ({
      ...prevState,
      intervalInfo: {
        ...prevState.intervalInfo,
        ...updatedFormValue
      }
    }));
  };

  return (
    <>
      <div className="picker-sub-container interval">
        <p className="forge-typography--body2">{I18n.t('repeat_every_label', { scope })}</p>
        <ForgeTextField className="number-field">
          <input
            data-testid="frequency-selector-interval-count"
            type="number"
            min={1}
            step={1}
            defaultValue={value.count}
            disabled={false}
            onChange={(event) => onValueChange('count', event.target.valueAsNumber)}
            autoComplete="off"
          />
        </ForgeTextField>
        <ForgeSelect
          data-testid="frequency-selector-interval-type"
          className="type-field"
          value={value.type}
          variant="plain"
          theme="primary"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onValueChange('type', event.target.value as IntervalTypes)
          }
          popoverIcon={false}
        >
          {intervalOptions.map((option) => (
            <ForgeOption key={`interval_${option.value}`} value={option.value}>
              {option.label}
            </ForgeOption>
          ))}

          <ForgeIcon slot="end" name="chevron_down" icon_type="button" theme="primary" />
        </ForgeSelect>
      </div>
      {shouldShowDaySelector && (
        <div className="picker-sub-container interval">
          <p className="forge-typography--body2">{I18n.t('repeat_on_label', { scope })}</p>
          {shouldShowWeekSelector && (
            <ForgeSelect
              data-testid="frequency-selector-interval-week"
              className="week-field"
              value={value.week}
              variant="plain"
              theme="primary"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onValueChange('week', event.target.value as WeekTypes)
              }
              density={'small'}
              popoverIcon={false}
            >
              {weekOptions.map((option) => (
                <ForgeOption key={`week_${option.value}`} value={option.value}>
                  {option.label}
                </ForgeOption>
              ))}

              <ForgeIcon slot="end" name="chevron_down" icon_type="button" theme="primary" />
            </ForgeSelect>
          )}

          <ForgeButtonToggleGroup
            data-testid="frequency-selector-interval-day"
            className="interval-days-toggle-group"
            value={value.days}
            on-forge-button-toggle-group-change={(event: CustomEvent) => {
              const daysVal = typeof event.detail === 'string' ? [event.detail] : event.detail;
              onValueChange('days', daysVal);
            }}
            multiple={canSelectMultipleDays}
            dense
          >
            {dayOptions.map((option) => (
              <ForgeButtonToggle key={`day_${option.value}`} value={option.value}>
                {option.label}
              </ForgeButtonToggle>
            ))}
          </ForgeButtonToggleGroup>
        </div>
      )}
    </>
  );
};

export default Interval;
